import { useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

/**
 * Live camera QR reader. Mounts on the div with id={containerId} and calls
 * onDetect(decodedText) on a successful scan. Caller is responsible for
 * pausing detection (we throttle internally too).
 *
 * @param {Object} props
 * @param {string} props.containerId   DOM id where the camera view will render
 * @param {boolean} props.active       when false, scanning is paused
 * @param {Function} props.onDetect    called with decoded QR string
 * @param {Function} props.onError     optional, called with Error on init failure
 */
export function QrScanner({ containerId, active, onDetect, onError }) {
  const instanceRef = useRef(null);
  const lastDetectRef = useRef(0);
  const onDetectRef = useRef(onDetect);
  onDetectRef.current = onDetect;

  // Mount/unmount the camera once.
  useEffect(() => {
    let cancelled = false;
    const qr = new Html5Qrcode(containerId, { verbose: false });
    instanceRef.current = qr;

    qr.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: { width: 240, height: 240 } },
      (decodedText) => {
        // Throttle: only fire once every 1.5s for the same/different code
        const now = Date.now();
        if (now - lastDetectRef.current < 1500) return;
        lastDetectRef.current = now;
        onDetectRef.current?.(decodedText);
      },
      () => {
        /* per-frame failure callback — quiet, this is normal */
      }
    ).catch((err) => {
      if (!cancelled) onError?.(err);
    });

    return () => {
      cancelled = true;
      const inst = instanceRef.current;
      if (inst && inst.getState && inst.getState() === 2 /* SCANNING */) {
        inst.stop().then(() => inst.clear()).catch(() => {});
      } else if (inst) {
        try { inst.clear(); } catch { /* noop */ }
      }
      instanceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerId]);

  // Pause/resume by toggling the underlying scan state
  useEffect(() => {
    const inst = instanceRef.current;
    if (!inst) return;
    if (active) {
      if (inst.getState && inst.getState() === 3 /* PAUSED */) {
        try { inst.resume(); } catch { /* noop */ }
      }
    } else if (inst.getState && inst.getState() === 2 /* SCANNING */) {
      try { inst.pause(true); } catch { /* noop */ }
    }
  }, [active]);

  return null;
}
